import dayjs from 'dayjs';
import { takeEvery, put, call, CallEffect, PutEffect } from 'redux-saga/effects';
import { GetUserConsentForClient, getUserConsentForClient } from '../../actions/consentActions';
import { Login, LoginAndRegisterActionType, SetUserEmail, setUserEmail } from '../../actions/loginAndRegisterActions';
import { RequestActionType, requestError, requestSuccess } from '../../actions/requestActions';
import callLogin from '../../api/login';
import { accessTokenLocalStoragePath, expiresAtLocalStoragePath } from '../../constants';
import { LoginResponse } from '../../models/commonModels';
import RequestActions from '../../models/RequestTypes';
import { RequestType } from '../../reducers/requestReducer';
import history from '../../store/history';
import { toErrorType } from '../isErrorType';

type TokenResponse = { accessToken: string; refreshToken: string; idToken: string; expiresAt: string };

export const redirectWithQuery = (url: string): void => {
    const searchQuery = window.location.search;
    history.push(`${url}${searchQuery}`);
};

export const shouldRedirectToClient = (searchQuery: string): boolean => {
    const hashes = searchQuery.slice(searchQuery.indexOf('?') + 1).split('&');
    if (hashes.length > 1 || !hashes.includes('')) {
        const redirectAttrs = hashes.reduce((redirectInfo, hash) => {
            const attr = hash.split('=');
            return { ...redirectInfo, [attr[0]]: attr[1] };
        }, {});
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return !!(redirectAttrs.client_id && redirectAttrs.redirect_uri);
    }
    return false;
};

export function updateTokenStorage({ idToken, accessToken, refreshToken, expiresAt }: TokenResponse): void {
    if (idToken) localStorage.setItem('airthingsAccounts.idToken', idToken);
    else localStorage.removeItem('airthingsAccounts.idToken');
    if (accessToken) localStorage.setItem(accessTokenLocalStoragePath, accessToken);
    else localStorage.removeItem(expiresAtLocalStoragePath);
    if (refreshToken) localStorage.setItem('airthingsAccounts.refreshToken', refreshToken);
    else localStorage.removeItem('airthingsAccounts.refreshToken');
    if (expiresAt) localStorage.setItem(expiresAtLocalStoragePath, expiresAt);
    else localStorage.removeItem(expiresAtLocalStoragePath);
}

type LoginSaga = Generator<
    CallEffect<LoginResponse> | PutEffect<GetUserConsentForClient> | PutEffect<SetUserEmail> | RequestActions,
    void,
    LoginResponse
>;
export function* loginSaga({ email, password }: Login): LoginSaga {
    const searchQuery = window.location.search;
    try {
        const response = yield call(callLogin, { email, password });
        const now = dayjs().subtract(5, 'minutes').unix();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const {
            expires_in: expiresIn,
            refresh_token: refreshToken,
            id_token: idToken,
            access_token: accessToken,
        } = response;
        const expiresAt = now + parseInt(expiresIn, 10);

        const parsedResponse = {
            refreshToken,
            accessToken,
            idToken,
            expiresAt: expiresAt.toString(),
        };

        yield put(setUserEmail(email));
        updateTokenStorage(parsedResponse);
        const willRedirect = shouldRedirectToClient(searchQuery);
        if (willRedirect) {
            yield put(getUserConsentForClient());
        } else {
            redirectWithQuery('/');
        }

        yield put(requestSuccess(RequestType.Login, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.Login, RequestActionType.Error));
    }
}

export default function* (): Generator {
    yield takeEvery(LoginAndRegisterActionType.LoginInit, loginSaga);
}
